import { useEffect, useRef } from 'react';

import type { SignupMethod } from './signup-method';

import { deriveSignupStartMethod } from './signup-method';
import { useAnalytics } from './use-analytics';

interface UseSignupStartedConversionOptions {
  /**
   * Every way into an account this surface offers. `signup_started`'s `method`
   * is derived from it — see {@link deriveSignupStartMethod}.
   */
  methods: readonly SignupMethod[];
  /**
   * Whether the visitor is in the signup funnel yet. Defaults to true, which
   * is right for a dedicated signup route: rendering it *is* the entry. A flow
   * that reaches its account step partway through passes the condition here
   * instead, so landing on the flow is not counted as entering the funnel.
   */
  enabled?: boolean;
  attributes?: Record<string, unknown>;
}

/**
 * Emits `signup_started` once for the life of the component.
 *
 * Shared by every surface that creates an account, so that the funnel's
 * denominator covers all of them: a surface emitting `signup_completed` with
 * no matching start makes the completion rate unbounded above 100%.
 */
export function useSignupStartedConversion({
  methods,
  enabled = true,
  attributes,
}: UseSignupStartedConversionOptions): void {
  const analytics = useAnalytics();
  const emitted = useRef(false);
  // Read through a ref so a fresh object literal at the call site cannot
  // re-run the effect; the emit-once guard covers the rest.
  const latest = useRef({ methods, attributes });
  latest.current = { methods, attributes };

  useEffect(() => {
    if (!analytics || !enabled || emitted.current) return;
    emitted.current = true;
    analytics.conversion({
      name: 'signup_started',
      attributes: {
        ...latest.current.attributes,
        // Stamped last, the way `conversion` stamps `side`: a `method` in the
        // call site's attributes must not win, or naming it there restores the
        // very hardcode this derivation exists to prevent.
        method: deriveSignupStartMethod(latest.current.methods),
      },
    });
  }, [analytics, enabled]);
}
